import { Copy } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

const copy = (t) => navigator.clipboard.writeText(t).then(() => toast.success("Copied"));

const ENV = `# .env.local on theexpatstudio.com
EXPAT_STUDIO_SECRET=<your signing secret above>
EXPAT_STUDIO_API=${API}`;

const IMPORT_ROUTE = `// app/api/expat-guides/import/route.ts
import crypto from "crypto";
import { NextResponse } from "next/server";
import { saveProduct, archiveProduct } from "@/lib/products"; // your DB / CMS

export async function POST(req: Request) {
  const body = await req.text();
  const ts = req.headers.get("x-expatstudio-timestamp") ?? "";
  const sig = req.headers.get("x-expatstudio-signature") ?? "";
  const expected = "sha256=" + crypto
    .createHmac("sha256", process.env.EXPAT_STUDIO_SECRET!)
    .update(\`\${ts}.\${body}\`).digest("hex");
  const fresh = Math.abs(Date.now() / 1000 - Number(ts)) < 300;
  if (!fresh || sig.length !== expected.length ||
      !crypto.timingSafeEqual(Buffer.from(sig), Buffer.from(expected)))
    return NextResponse.json({ error: "invalid signature" }, { status: 401 });

  const { event, product, pack } = JSON.parse(body);
  if (event === "product.upsert") await saveProduct(product);   // upsert by product.id
  if (event === "product.archive") await archiveProduct(product.id);
  if (event === "pack.upsert") await savePack(pack);            // pack.guides[].product_id, pack.price
  if (event === "pack.archive") await archivePack(pack.id);
  return NextResponse.json({ ok: true });                        // "ping" just returns ok
}`;

const CHANGELOG = `// Show "What's new" on a product page (public, no secret needed)
// product.changelog_url       -> ready-made branded page (add ?embed=1 for iframe)
// product.changelog_json_url  -> JSON: { current_edition, last_updated, editions[] }

<iframe src={\`\${product.changelog_url}?embed=1\`} style={{ width: "100%", height: 480, border: 0 }} />

// or render it yourself:
const log = await fetch(product.changelog_json_url, { next: { revalidate: 3600 } }).then(r => r.json());
log.editions.map(e => <article key={e.edition}><h3>Edition {e.edition}: {e.headline}</h3><p>{e.note}</p></article>);`;

const LINKS = `// lib/expat-studio.ts — call after a successful payment
import crypto from "crypto";

export async function getDownloadLinks(productId: string, orderId: string, email: string,
                                       items = ["pdf", "app"], amount?: number) {
  const variant = items.includes("app") ? "bundle" : "pdf";
  const body = JSON.stringify({ product_id: productId, order_id: orderId, email, items, amount, variant });
  const ts = Math.floor(Date.now() / 1000).toString();
  const sig = "sha256=" + crypto
    .createHmac("sha256", process.env.EXPAT_STUDIO_SECRET!)
    .update(\`\${ts}.\${body}\`).digest("hex");
  const r = await fetch(\`\${process.env.EXPAT_STUDIO_API}/store/download-links\`, {
    method: "POST", body,
    headers: { "Content-Type": "application/json",
               "X-ExpatStudio-Timestamp": ts, "X-ExpatStudio-Signature": sig },
  });
  if (!r.ok) throw new Error("Could not create download links");
  return r.json(); // { pdf_url, app_url, pdf_password, expires_at, email_sent }
}
// PDF-only variant  -> items: ["pdf"]
// Bundle variant    -> items: ["pdf", "app"]
// Pack purchase     -> send { pack_id, order_id, email, amount } instead of product_id;
//                      response.guides[] has pdf_url/app_url per town, one pdf_password`;

const Snippet = ({ title, code, testId }) => (
  <div className="bg-[#1C181D] text-[#EFE6F3] p-6 relative">
    <button data-testid={`copy-${testId}`} onClick={() => copy(code)} className="absolute top-4 right-4 text-[#C59B27] hover:text-white transition-colors"><Copy className="w-4 h-4" /></button>
    <p className="eyebrow mb-3">{title}</p>
    <pre className="font-mono-accent text-[11px] leading-5 overflow-x-auto whitespace-pre">{code}</pre>
  </div>
);

export const IntegrationGuide = () => (
  <div className="space-y-6" data-testid="integration-guide">
    <div className="bg-[#F4EFE6] border border-[#E2D9CC] p-6 text-sm space-y-2">
      <p className="eyebrow mb-2">What theexpatstudio.com needs</p>
      <p><b className="text-[#3B1B48]">1.</b> Two environment variables (below).</p>
      <p><b className="text-[#3B1B48]">2.</b> One API route that receives products: <code className="font-mono-accent text-xs">/api/expat-guides/import</code></p>
      <p><b className="text-[#3B1B48]">3.</b> Somewhere to save products (your database or CMS) and a product page that renders them.</p>
      <p><b className="text-[#3B1B48]">4.</b> Your checkout (e.g. Stripe). After payment succeeds, call <code className="font-mono-accent text-xs">getDownloadLinks()</code>. This app then emails the buyer a branded message with their links and their personal PDF password (links last 72 hours, 10 downloads each). The response also returns <code className="font-mono-accent text-xs">pdf_password</code> so you can show it on your thank-you page. Pass <code className="font-mono-accent text-xs">send_email: false</code> if you'd rather send your own email.</p>
    </div>
    <Snippet title="1 · Environment" code={ENV} testId="env-snippet" />
    <Snippet title="2 · Receive products" code={IMPORT_ROUTE} testId="import-snippet" />
    <Snippet title="4 · Buyer download links" code={LINKS} testId="links-snippet" />
    <Snippet title="5 · What's new on product pages" code={CHANGELOG} testId="changelog-snippet" />
  </div>
);
